import React, { useEffect } from "react";
import Navbar from "../components/layout/Navbar";
import Reveal from "../components/common/Reveal";
import Footer from "../components/layout/Footer";

const RAZORPAY_TERMS_URL = "https://merchant.razorpay.com/policy/RgoOAkclGqDTgr/terms";
const RAZORPAY_REFUND_URL = "https://merchant.razorpay.com/policy/RgoOAkclGqDTgr/refund";
const RAZORPAY_SHIPPING_URL = "https://merchant.razorpay.com/policy/RgoOAkclGqDTgr/shipping";

const TermsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen bg-primary">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-accentGold/5 to-accentNavy/10 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accentNavy/20 via-transparent to-accentGold/15" />
        <div className="absolute -right-32 top-1/2 h-96 w-96 rounded-full bg-accentGold/30 blur-3xl" />
        <div className="absolute -left-32 bottom-1/4 h-96 w-96 rounded-full bg-accentNavy/20 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <a
              href="#home"
              className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-accentNavy/70 transition-colors duration-200 hover:text-accentGold"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Home
            </a>
          </Reveal>

          <Reveal delay={120}>
            <h1 className="mb-4 font-display text-4xl leading-tight text-accentNavy sm:text-5xl lg:text-6xl">
              Terms of Service
            </h1>
            <p className="text-lg text-accentNavy/70">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative bg-gradient-to-b from-primary via-primary/95 to-primary/90 py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="space-y-8 text-accentNavy/80 leading-relaxed">
            <Reveal>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">1. Acceptance of Terms</h2>
                <p className="text-base leading-relaxed">
                  By accessing and using Ukulele Seekho's services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">2. Course Enrollment</h2>
                <p className="mb-4 text-base leading-relaxed">
                  When you enroll in a course through Ukulele Seekho, you agree to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base">
                  <li>Attend scheduled classes regularly</li>
                  <li>Respect the learning environment and fellow students</li>
                  <li>Follow the instructor's guidance and curriculum</li>
                  <li>Complete assignments and practice as recommended</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={180}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">3. Payment Terms</h2>
                <p className="mb-4 text-base leading-relaxed">
                  All payments for courses are processed securely through Razorpay. By making a payment, you agree to Razorpay's terms and conditions.
                </p>
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-semibold text-accentNavy/70">Payment-related policies:</p>
                  <ul className="ml-6 space-y-2">
                    <li className="text-base">
                      <a
                        href={RAZORPAY_TERMS_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accentGold hover:underline font-medium"
                      >
                        Razorpay Terms of Service
                      </a>
                    </li>
                    <li className="text-base">
                      <a
                        href={RAZORPAY_REFUND_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accentGold hover:underline font-medium"
                      >
                        Refund Policy
                      </a>
                    </li>
                    <li className="text-base">
                      <a
                        href={RAZORPAY_SHIPPING_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accentGold hover:underline font-medium"
                      >
                        Shipping Policy
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </Reveal>

            <Reveal delay={240}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">4. Intellectual Property</h2>
                <p className="text-base leading-relaxed">
                  All course materials, including but not limited to videos, PDFs, tablatures, and instructional content, are the intellectual property of Ukulele Seekho. These materials are provided for your personal use only and may not be shared, distributed, or reproduced without explicit written permission.
                </p>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">5. Cancellation and Refunds</h2>
                <p className="mb-4 text-base leading-relaxed">
                  Refund requests are subject to our refund policy. For detailed information about refunds, please refer to our{" "}
                  <a
                    href={RAZORPAY_REFUND_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accentGold hover:underline font-medium"
                  >
                    Refund Policy
                  </a>
                  .
                </p>
              </div>
            </Reveal>

            <Reveal delay={360}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">6. Limitation of Liability</h2>
                <p className="text-base leading-relaxed">
                  Ukulele Seekho shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.
                </p>
              </div>
            </Reveal>

            <Reveal delay={420}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">7. Changes to Terms</h2>
                <p className="text-base leading-relaxed">
                  We reserve the right to modify these terms at any time. We will notify users of any significant changes via email or through our website. Continued use of the service after changes constitutes acceptance of the new terms.
                </p>
              </div>
            </Reveal>

            <Reveal delay={480}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">8. Contact Information</h2>
                <p className="text-base leading-relaxed">
                  If you have any questions about these Terms of Service, please contact us at{" "}
                  <a
                    href="mailto:admin@ukuleleseekho.com"
                    className="text-accentGold hover:underline font-medium"
                  >
                    admin@ukuleleseekho.com
                  </a>
                  .
                </p>
              </div>
            </Reveal>
          </div>

          {/* Back Button */}
          <Reveal delay={540}>
            <div className="mt-12 flex items-center justify-center">
              <a
                href="#home"
                className="inline-flex items-center gap-2 rounded-full border border-accentNavy/20 bg-white/80 px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-accentNavy transition-all duration-300 hover:border-accentGold hover:bg-accentGold hover:text-white"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Home
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;

